/* WARNING: these types have been manually inferred from their
 * usage in bookings project code, and might note be complete or 100% accurate.
 * in a future iteration, most of them could be autogenerated from an API documentation.
 */
import { NextRouter } from 'next/router'
import { NextPage } from 'next/types'
import { ReactElement, ReactNode } from 'react'

import { ErrorOrResponseData } from '@/utils/errorMessageHandler'
import { Stores } from '@/utils/StoresContext'

interface BaseOffer {
  id: number
  name: string
  name_translations: TranslatableValues
  description?: TranslatableValues
  has_prepayment: boolean
  charge_per_guests?: number | null
  picture_url?: string | null
}

export type Phase = 'waiting_payment' | 'waiting_imprint' | 'waiting_list'

export interface Charge {
  quotation_id?: number
  payment_intent_id?: number
  payment_method_id?: number
  setup_intent_id?: string
  stripe_client_secret?: string
  amount?: number
  currency?: string
  successUrl?: string
}

export interface Quotation {
  id?: number
  amount?: number
  currency?: string
  clientSecret?: string
}

export interface TurnTimes {
  '2_pax_slots': number
  '4_pax_slots': number
  '6_pax_slots': number
  '8_pax_slots': number
  '10_pax_slots': number
  '12_pax_slots': number
}

export type RoomAvailability = Record<number, number[]> // pax -> room ids

export namespace Bookings {
  export interface Capacity {
    min: number
    max: number
    total_per_slot: number
    turn_times: TurnTimes
    show_turn_times: boolean
  }

  export interface WishOffer {
    offer_id: number
    count: number
  }

  export interface Room {
    id: number
    name: string
    name_translations: TranslatableValues
  }

  export interface SlotOffer {
    id: number
    has_prepayment: boolean
    capacity: {
      min: number | null // this represents the minimum selection for the offer (number of offers)
      max: number | null // this represents the maximum selection for the offer (number of offers)
      total_per_slot: number | null
    }
    config: {
      is_limited_to_pax: boolean
      is_same_for_all: boolean
      min_pax_available: number // this represents the pax limits for which the offer is available
      max_pax_available: number | null // this represents the pax limits for which the offer is available
    }
    stock: number
    possible_guests: number[]
    available_rooms?: RoomAvailability
    bookable_to?: string | null
    bookable_from?: string | null
  }

  export interface ShiftOffer
    extends BaseOffer,
      Omit<SlotOffer, 'possible_guests' | 'has_duration' | 'available_rooms'> {
    stock_id: number
    count: number
    is_unlimited: boolean
    total: number
    has_specific_rooms: boolean
    is_private: boolean
    rooms: Room['id'][]
  }

  export type SelectedOffer = ShiftOffer & SlotOffer

  export interface Offer extends BaseOffer {
    description: TranslatableValues
    date_from: string
    date_to: string | null
    is_active: boolean
    is_highlighted: boolean
    is_private: boolean
    is_on_first_step: boolean
    position: number
    type: string | null
    created_at: string
    updated_at: string
    deleted_at: string | null
    weekdays: Record<string, boolean>
    picture: {
      disk: string
      id: number
      is_public: boolean
      mime_type: string
      name: string
      url: string
    }
    has_duration: boolean
    turn_times: TurnTimes
    is_squeezed: boolean
  }

  export interface Slot {
    slot_name: string
    name: string
    closed: boolean
    offers?: SlotOffer[]
    available_rooms?: RoomAvailability
    possible_guests: number[]
    marked_as_full?: boolean
    waitlist_possible_guests: number[]
    capacity?: Capacity
    disabled?: boolean
  }

  export interface ShiftChargeParam {
    is_web_booking_askable?: boolean
    min_guests?: number
    charge_per_guests?: number
  }

  export interface Shift {
    confirmation?: {
      is_auto: boolean
      is_auto_until: number
    }
    comment?: TranslatableValues
    id: number
    shift_slots: Slot[]
    open: string
    close: string
    name: string
    name_translations: TranslatableValues
    cancelation_param?: {
      enduser_cancelable_reference?: 'shift' | string
      enduser_cancelable_before: number
    }
    offers?: ShiftOffer[]
    bookable_rooms?: number[]
    charge_param?: ShiftChargeParam
    prepayment_param?: ShiftChargeParam
    marked_as_full?: boolean
    is_offer_required: boolean
    offer_required_from_pax?: number | null
    total?: number
    capacity?: Capacity
    bookable_from?: string | null
    bookable_to?: string | null
  }

  export type SummaryOffer = Pick<SlotOffer, 'id' | 'possible_guests' | 'bookable_from' | 'bookable_to'>

  export interface SummaryShift
    extends Pick<
      Shift,
      | 'id'
      | 'name'
      | 'name_translations'
      | 'bookable_from'
      | 'bookable_to'
      | 'is_offer_required'
      | 'offer_required_from_pax'
    > {
    possible_guests: number[]
    waitlist_possible_guests: number[]
    closed: boolean
    offers: SummaryOffer[]
  }

  interface GenericDay<T extends Bookings.Shift | Bookings.SummaryShift> {
    date: string
    shifts: T[]
    bookable_from?: string | null
    bookable_to?: string | null
    isOpen?: boolean | 'closed'
  }

  export type Day = GenericDay<Bookings.Shift>

  export type SummaryDay = GenericDay<Bookings.SummaryShift>

  export interface RestaurantInfo {
    name: string
    email: string
    website: string
    cancelable_before: number
    mediator_contact: string
    company: string
    city: string
    capital: string
    rcs: string
    tva: string
    headquarters: string
  }

  export interface Product {
    price: number
    quantity: number
    id: number
    sku: string
    name: string
    description: string
    expired_after: number
  }

  export interface Optin {
    type: 'market_mail' | 'market_sms' | 'review_mail' | 'review_sms'
    value: boolean
  }

  export interface CustomField {
    slug: string
    displayType: 'text' | 'select' | 'radio' | 'checkbox'
    title: string
    options: { value: string; text: string }[]
  }

  export interface Wish {
    day: string
    pax: number
    slot: string | undefined
    waiting_list: boolean
    offers: WishOffer[]
    room_id: number | null
    lang?: string
  }

  export interface PreviousWish extends Partial<Omit<Wish, 'offers'>> {
    offers?: WishOffer[]
  }

  export declare type UnmodifiableReasons =
    | 'charge'
    | 'no_longer_time'
    | 'out_of_shift'
    | 'shift_feature_disabled'
    | 'unprocessable_phase'

  export interface BookingDetails {
    is_modifiable_by_enduser: boolean
    unmodifiable_by_enduser_reasons: UnmodifiableReasons[]
    id: number
    uuid: string
    type: string
    partner_id: string
    restaurant_id: number
    resto_id: number
    resto_name: string
    day: string
    time: string
    nb_guests: number
    slots_count: unknown
    civility: string | 'mrs' | 'mr' | 'mx'
    firstname: string
    lastname: string
    country: string
    phone_number: string
    email: string
    comment: string
    lang: AvailableLanguages
    status: string
    shift_slot_id: number
    custom_field: Record<string, unknown>
    booking_offers: unknown[]
    shift_date: string
    wish: {
      booking_id: number
      booking_room_id: number | null
    }
    customersheet: {
      optins: Optin[]
    }
    customer_sheet_id: number
    charge: Charge
    // incomplete
  }
}

const ALL_WISH_FILTERS = ['area', 'slot', 'pax', 'calendar'] as const
export type WishFilter = (typeof ALL_WISH_FILTERS)[number]
export function isWishFilter(value: string): value is WishFilter {
  return ALL_WISH_FILTERS.includes(value as WishFilter)
}

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (
    page: ReactElement,
    additionalProps: {
      stores: Stores
      router: NextRouter
      t: (key: string) => string
      // goTo: (path: string) => void
    }
  ) => ReactNode
}

// - "arrangement": contains experiences
// - "value": contains discounts
export type VoucherType = 'arrangement' | 'value'

interface BaseVoucherCode {
  code: string
  nameTranslations?: TranslatableValues
  originalAmount: number
}

export interface ValidatedVoucherCode extends BaseVoucherCode {
  type: VoucherType
}

export interface BookingVoucherCode extends BaseVoucherCode {
  usedAmount: number
}

interface AbstractVoucherParam {
  id: number
  restaurant_payment_provider_id: number | null
}

interface V1VoucherParam extends AbstractVoucherParam {
  version: 'v1'
  tablebooker_shop_id: null
}

interface TableBookerVoucherParam extends AbstractVoucherParam {
  version: 'tablebooker'
  tablebooker_shop_id: string
}

interface MigratingToTableBookerVoucherParam extends AbstractVoucherParam {
  version: 'migrating-to-tablebooker'
  tablebooker_shop_id: string | null
}

export type VoucherParam = V1VoucherParam | TableBookerVoucherParam | MigratingToTableBookerVoucherParam

export const isTablebookerVoucherParam = (voucherParam?: VoucherParam): voucherParam is TableBookerVoucherParam =>
  voucherParam?.version === 'tablebooker' && !!voucherParam.tablebooker_shop_id

export interface ValidateVoucherResponse {
  is_valid: true
  reason: string | null
  voucherCode: string
  type: VoucherType
  name_translations: TranslatableValues | null
  value: number
  valid_until: string | null
}

export interface PersonalFormData {
  firstname: string
  lastname: string
  civility: string
  phone: string
  email: string
  optins: Bookings.Optin[]
  country: string
  lang: string
}

export interface CustomerSheet {
  relatedBookingId: number | null
  id: number | null
  optins: Bookings.Optin[] | null
  relatedBookingStatus?: string
}

export interface VoucherCodeApi {
  name_translations: TranslatableValues
  code: string
  original_amount: number
  used_amount: number
}

export interface CreateBookingPayload extends Omit<PersonalFormData, 'phone' | 'optins'> {
  charge?: Charge
  comment: string
  country: string
  custom_field?: Record<string, string>
  customersheet?: PersonalFormData
  day: string
  nb_guests: number
  offers: Bookings.WishOffer[]
  partner_id: string
  phase?: Phase
  phone_number: string
  prescriber_id?: string
  marketing_trackable?: { id: string; source: string }
  source_restaurant_id?: number
  status?: string
  time: string | undefined
  type: string | null
  voucher_codes?: Pick<VoucherCodeApi, 'code'>[]
  wish?: { booking_room_id: number | null }
}

export interface CreateBookingResponse extends ErrorOrResponseData {
  id: number
  uuid: string
  sha256: string
  requires_action: boolean
  customersheet: CustomerSheet
  status: string
  charge?: { stripe_client_secret: string }
  expires_at?: number
  voucher_codes?: VoucherCodeApi[]
}

export interface PendingBooking {
  phase: Phase
  status: Bookings.BookingDetails['status']
  customer_sheet_id: Bookings.BookingDetails['customer_sheet_id']
}
